import { useMemo, useState } from 'react';
import { formatDate, formatDateTime } from '../../../lib/format';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { AlertTriangle, Monitor, RotateCcw, ShieldOff } from 'lucide-react';
import type { AuditRow, CursorPage, PlatformUserDetail, SessionRow } from '@dru/types';
import { BRAND, SCOPE_LABEL, USER_STATUS_LABEL } from '@dru/types';
import {
  Alert, Button, ConfirmDialog, Detail, DetailHeader, DetailSection, InfoTip, SearchSelect, StatusBadge, Tabs,
  TextSkeleton, useToast, type Tone,
} from '@dru/ui';
import { api } from '../../../lib/api';
import { useQuery } from '../../../lib/useQuery';
import { useAuth, errorMessage } from '../../../lib/auth';
import { Dialog } from '../../../components/Dialog';
import { useSignInAs, whyNotImpersonable } from '../components/ImpersonateDialog';
import { useBreadcrumbTitle } from '../../../layouts/BreadcrumbContext';
import { ActivityTable, NotForYourRole } from './Audit';
import { roleHint } from '../components/SecurityRoleHints';

const STATUS_TONE: Record<string, Tone> = {
  ACTIVE: 'covered', INVITED: 'pending', SUSPENDED: 'overdue', DEACTIVATED: 'neutral',
};

/**
 * One platform operator, in full.
 *
 * Identity, what they can do — grouped by domain rather than a wall of a
 * hundred chips — where they are signed in from, and what they have done.
 * The drawer this replaces showed the same facts in one scrolling column,
 * which is fine for a glance and useless for an audit.
 */
export function UserDetail() {
  const { permissions } = useAuth();
  if (!(permissions.has('user.read'))) {
    return <NotForYourRole title="Platform operator" what="Platform staff accounts are managed by platform administrators." />;
  }
  return <PlatformUserDetail />;
}

function PlatformUserDetail() {
  const { id = '' } = useParams();
  const nav = useNavigate();
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') ?? 'overview';
  const { permissions: held, principal } = useAuth();
  const toast = useToast();

  const user = useQuery<PlatformUserDetail>(() => api.get(`/admin/platform-users/${id}`), [id]);
  const sessions = useQuery<{ items: SessionRow[] }>(() => api.get(`/admin/platform-users/${id}/sessions`), [id]);
  const trail = useQuery<CursorPage<AuditRow>>(() => api.get(`/audit?actorId=${id}&limit=50`), [id]);
  const [revoking, setRevoking] = useState(false);
  const [suspending, setSuspending] = useState(false);
  const [reinstating, setReinstating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const u = user.data;
  useBreadcrumbTitle(u?.fullName ?? u?.email);
  const live = (sessions.data?.items ?? []).filter((s) => s.live);
  const canSuspend = held.has('user.suspend');
  const canInvite = held.has('user.create');
  const canAssign = held.has('user.role-assign');
  const canImpersonate = held.has('platform.impersonate');
  const [inviting, setInviting] = useState(false);
  const [changingRole, setChangingRole] = useState(false);
  const signIn = useSignInAs();
  const notImpersonable = u ? whyNotImpersonable(u, principal?.id) : null;
  const isMe = u?.id === principal?.id;

  async function resendInvitation() {
    setInviting(true); setError(null);
    try {
      const r = await api.post<{ email: string; expiresAt: string }>(`/admin/platform-users/${id}/invite`);
      toast.toast('success', `Invitation sent to ${r.email}. The previous link no longer works.`);
    } catch (e) { setError(errorMessage(e)); } finally { setInviting(false); }
  }

  const setTab = (key: string) => {
    const next = new URLSearchParams(params); next.set('tab', key); setParams(next, { replace: true });
  };

  // Permissions read better as "employee: read, create, update…" than as
  // a hundred dotted strings in no order.
  const grouped = useMemo(() => {
    const map = new Map<string, string[]>();
    for (const p of u?.permissions ?? []) {
      const [domain, ...rest] = p.split('.');
      const verb = rest.join('.') || p;
      if (!map.has(domain!)) map.set(domain!, []);
      map.get(domain!)!.push(verb);
    }
    return [...map.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [u?.permissions]);

  return (
    <div className="space-y-5">
      <DetailHeader
        eyebrow="Platform operator"
        title={u?.fullName ?? 'Loading…'}
        badges={u && (
          <>
            <StatusBadge tone={STATUS_TONE[u.status] ?? 'neutral'}>{USER_STATUS_LABEL[u.status] ?? u.status}</StatusBadge>
            <StatusBadge tone={u.mfaEnabled ? 'covered' : 'pending'}>{u.mfaEnabled ? 'Two-factor on' : 'No second factor'}</StatusBadge>
          </>
        )}
        subtitle={u && [u.email, u.roles.map((r) => r.name).join(', ') || 'no role', `${live.length} live session${live.length === 1 ? '' : 's'}`].join(' · ')}
        // Everyday acts only. Suspending and ending sessions lock a person out,
        // so they live apart in "Stop access" below, not beside "Change role"
        // where a slip of the cursor reaches them.
        actions={u && (canInvite || canAssign || canImpersonate) && (
          <>
            {u.status === 'INVITED' && canInvite && (
              <Button variant="secondary" onClick={resendInvitation} loading={inviting}>Resend invitation</Button>
            )}
            {canAssign && !isMe && (
              <Button variant="secondary" onClick={() => setChangingRole(true)}>Change role</Button>
            )}
            {canImpersonate && !isMe && (
              <Button
                variant="secondary"
                onClick={() => void signIn.signInAs(`/admin/users/${u.id}/impersonate`)}
                loading={signIn.busy !== null}
                disabled={!!notImpersonable}
                title={notImpersonable ?? 'Sign in as this person'}
              >
                Sign in as
              </Button>
            )}
          </>
        )}
      />

      {user.error && <Alert tone="danger">{user.error}</Alert>}
      {u && canImpersonate && notImpersonable && !isMe && (
        <p className="text-caption text-neutral-600">Sign in as is unavailable: {notImpersonable}</p>
      )}
      {signIn.error && <Alert tone="danger">{signIn.error}</Alert>}
      {u?.status === 'SUSPENDED' && (
        <Alert tone="danger" title="Suspended — cannot sign in">
          {u.suspendedReason ? `Reason given: ${u.suspendedReason}` : 'No reason was recorded.'}
        </Alert>
      )}
      {error && <Alert tone="danger">{error}</Alert>}

      <Tabs
        tabs={[
          { key: 'overview', label: 'Identity' },
          { key: 'access', label: 'Access', hint: u?.permissions.length },
          { key: 'sessions', label: 'Sessions', hint: live.length || undefined },
          { key: 'activity', label: 'Activity' },
        ]}
        active={tab}
        onChange={setTab}
      />

      {user.loading && <TextSkeleton lines={6} />}

      {u && tab === 'overview' && (
        <div className="grid grid-cols-1 gap-4 lg:grid-cols-2 [&>*]:min-w-0">
          <DetailSection title="Identity">
            <div className="grid gap-3 sm:grid-cols-2">
              <Detail label="Full name" value={u.fullName} />
              <Detail label="Email" value={u.email} mono />
              <Detail label="User type" value="Platform operator" />
              <Detail label="Works for" value={`${BRAND.name} (platform)`} />
              <Detail label="Status" value={USER_STATUS_LABEL[u.status] ?? u.status} />
              <Detail label="Created" value={formatDate(u.createdAt)} />
              <Detail label="Last signed in" value={u.lastLoginAt ? formatDateTime(u.lastLoginAt) : 'Never'} />
              <Detail label="Failed sign-ins" value={String(u.failedLogins)} />
              {u.lockedUntil && <Detail label="Locked until" value={formatDateTime(u.lockedUntil)} />}
            </div>
          </DetailSection>

          <DetailSection title="Second factor">
            <div className="grid gap-3 sm:grid-cols-2">
              <Detail label="Two-factor" value={u.mfaEnabled ? 'On' : 'Not set up'} />
              <Detail label="Since" value={u.mfaActivatedAt ? formatDate(u.mfaActivatedAt) : 'Not set up'} />
              <Detail label="Acts that need it" value={String(u.stepUpPermissions.length)} />
            </div>
            {u.mfaGap && (
              <Alert tone="warning" className="mt-3">
                <AlertTriangle className="mr-1.5 inline h-4 w-4 align-text-bottom" aria-hidden />
                Holds {u.stepUpPermissions.length} {u.stepUpPermissions.length === 1 ? 'act that needs' : 'acts that need'} a second factor and has not set one up. Only they can enrol, from their own Security page — a seed must never pass through somebody else&rsquo;s hands.
              </Alert>
            )}
          </DetailSection>

          <DetailSection title="Roles and scope" className="lg:col-span-2">
            {u.roles.length === 0 ? (
              <p className="text-small text-warning-600">No role. This account can sign in and do nothing.</p>
            ) : (
              <ul className="divide-y divide-neutral-100">
                {u.roles.map((r) => (
                  <li key={r.key} className="flex flex-wrap items-center justify-between gap-2 py-2">
                    <span className="text-small font-medium text-neutral-900">{r.name}</span>
                    <span className="text-caption text-neutral-600">
                      Reaches: {SCOPE_LABEL[r.scopeType as keyof typeof SCOPE_LABEL] ?? r.scopeType}
                      {r.scopeIds.length ? ` · limited to ${r.scopeIds.length}` : ''}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </DetailSection>

          {canSuspend && !isMe && (u.status === 'ACTIVE' || u.status === 'SUSPENDED' || live.length > 0) && (
            <section aria-labelledby="stop-access" className="rounded-card border border-danger-600/30 bg-white p-4 lg:col-span-2">
              <h2 id="stop-access" className="text-caption font-semibold uppercase tracking-wider text-danger-600">
                {u.status === 'SUSPENDED' ? 'Restore access' : 'Stop access'}
              </h2>
              <div className="mt-3 divide-y divide-neutral-100">
                {live.length > 0 && (
                  <div className="flex flex-wrap items-center justify-between gap-3 py-3 first:pt-0">
                    <p className="min-w-0 flex-[1_1_18rem] text-small text-neutral-700">
                      <span className="font-medium text-neutral-900">End all {live.length} live {live.length === 1 ? 'session' : 'sessions'}.</span>{' '}
                      Signs them out everywhere; the account stays usable. For a lost laptop or a shared screen.
                    </p>
                    <Button variant="secondary" onClick={() => setRevoking(true)}>End sessions</Button>
                  </div>
                )}
                {u.status === 'ACTIVE' && (
                  <div className="flex flex-wrap items-center justify-between gap-3 py-3 last:pb-0">
                    <p className="min-w-0 flex-[1_1_18rem] text-small text-neutral-700">
                      <span className="font-medium text-neutral-900">Suspend this account.</span>{' '}
                      They cannot sign in until reinstated, and every session ends now. Needs a written reason.
                    </p>
                    <Button variant="danger" onClick={() => setSuspending(true)} icon={<ShieldOff className="h-4 w-4" aria-hidden />}>Suspend</Button>
                  </div>
                )}
                {u.status === 'SUSPENDED' && (
                  <div className="flex flex-wrap items-center justify-between gap-3 py-3 last:pb-0">
                    <p className="min-w-0 flex-[1_1_18rem] text-small text-neutral-700">
                      <span className="font-medium text-neutral-900">Reinstate this account.</span>{' '}
                      They can sign in again with their existing password. Old sessions stay ended.
                    </p>
                    <Button variant="secondary" onClick={() => setReinstating(true)} icon={<RotateCcw className="h-4 w-4" aria-hidden />}>Reinstate</Button>
                  </div>
                )}
              </div>
            </section>
          )}
        </div>
      )}

      {u && tab === 'access' && (
        <DetailSection
          phoneFolded
          title={`What they can do (${u.permissions.length})`}
          action={(
            <InfoTip label="About what they can do">
              Computed from the roles held now, not from the token they are carrying. Highlighted acts demand a second factor.
            </InfoTip>
          )}
        >
          <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-3">
            {grouped.map(([domain, verbs]) => (
              <div key={domain} className="rounded-card border border-neutral-100 p-2.5">
                <dt className="text-caption font-semibold uppercase tracking-wide text-neutral-600">{domain}</dt>
                <dd className="mt-1 flex flex-wrap gap-1">
                  {verbs.map((v) => {
                    const full = `${domain}.${v}`;
                    const stepUp = u.stepUpPermissions.includes(full);
                    return (
                      <span key={v} title={stepUp ? 'Needs a second factor' : undefined}
                        className={`rounded-badge px-1.5 py-0.5 font-mono text-micro ${stepUp ? 'bg-primary-100 text-primary-700' : 'bg-neutral-100 text-neutral-600'}`}>
                        {v}
                      </span>
                    );
                  })}
                </dd>
              </div>
            ))}
          </dl>
        </DetailSection>
      )}

      {u && tab === 'sessions' && (
        <DetailSection title="Sessions" phoneFolded action={canSuspend && live.length > 0 ? (
          <Button size="sm" variant="secondary" onClick={() => setRevoking(true)}>End all {live.length}</Button>
        ) : undefined}>
          {sessions.loading && <TextSkeleton lines={2} />}
          {!sessions.loading && (sessions.data?.items.length ?? 0) === 0 && <p className="text-small text-neutral-500">No sessions: this account has never signed in.</p>}
          <ul className="space-y-1">
            {(sessions.data?.items ?? []).map((s) => (
              <li key={s.id} className="flex flex-wrap items-baseline gap-x-2 rounded-input border border-neutral-200 px-2.5 py-1.5 text-caption">
                <Monitor className="h-3.5 w-3.5 shrink-0 text-neutral-400" aria-hidden />
                <span className="font-mono text-neutral-700">{s.ip ?? 'unknown IP'}</span>
                <span className="truncate text-neutral-500">{s.userAgent ?? '—'}</span>
                <span className="ml-auto text-neutral-500">last used {formatDateTime(s.lastUsedAt)}</span>
                <StatusBadge tone={s.live ? 'covered' : 'neutral'}>{s.live ? 'Live' : s.revokedAt ? 'Ended' : 'Expired'}</StatusBadge>
              </li>
            ))}
          </ul>
        </DetailSection>
      )}

      {u && tab === 'activity' && (
        <DetailSection title="Activity">
          <ActivityTable
            rows={trail.data?.items ?? []}
            loading={trail.loading}
            error={trail.error}
            onRetry={trail.reload}
            showRecord
            emptyHint="Every act this operator performs appears here."
          />
        </DetailSection>
      )}

      {changingRole && u && (
        <ChangeRoleDialog
          userId={u.id}
          fullName={u.fullName}
          current={u.roles.map((r) => r.key)}
          currentLabels={u.roles.map((r) => r.name)}
          onClose={() => setChangingRole(false)}
          onDone={(name) => {
            setChangingRole(false);
            toast.toast('success', `${u.fullName} is now ${name}. Their open sessions were ended so it applies at once.`);
            user.reload(); sessions.reload();
          }}
        />
      )}
      {revoking && (
        <ConfirmDialog
          title="End every live session?"
          what="They are signed out everywhere and must sign in again. The account itself is untouched."
          affects={`${live.length} live ${live.length === 1 ? 'session' : 'sessions'}.`}
          irreversible="Their current access token stays valid for up to fifteen minutes; it is stateless by design."
          confirmLabel="End sessions" tone="danger" requireReason minReason={10} reasonLabel="Why are these being ended?"
          onCancel={() => setRevoking(false)}
          // Throws on refusal, so the dialog stays open saying why rather
          // than closing as if it had worked.
          onConfirm={async (reason) => {
            const res = await api.post<{ revoked: number }>(`/admin/platform-users/${id}/revoke-sessions`, { reason });
            toast.toast('success', `${res.revoked} sessions ended.`);
            setRevoking(false); sessions.reload(); user.reload();
          }}
        />
      )}
      {suspending && u && (
        <ConfirmDialog
          title={`Suspend ${u.fullName}?`}
          what="They can no longer sign in, and every live session ends immediately."
          affects={`${u.roles.map((r) => r.name).join(', ') || 'No role'} · ${live.length} live ${live.length === 1 ? 'session' : 'sessions'}. Anything only they could do stops until somebody else picks it up.`}
          irreversible="Records they signed stay valid — suspension never rewrites history. It can be reversed with Reinstate."
          confirmLabel="Suspend account" tone="danger" requireReason minReason={10} reasonLabel="Reason"
          reasonHint="Recorded in the audit trail and shown on this account."
          onCancel={() => setSuspending(false)}
          onConfirm={async (reason) => {
            await api.post(`/admin/users/${id}/suspend`, { reason });
            toast.toast('success', `${u.fullName} suspended.`);
            setSuspending(false); user.reload(); sessions.reload();
            nav(`/admin/users/${id}`, { replace: true });
          }}
        />
      )}
      {reinstating && u && (
        <ConfirmDialog
          title={`Reinstate ${u.fullName}?`}
          what="They can sign in again with their existing password and their current role."
          affects={u.suspendedReason ? `Suspended because: ${u.suspendedReason}` : undefined}
          confirmLabel="Reinstate account" requireReason minReason={10} reasonLabel="Reason"
          reasonHint="Recorded in the audit trail."
          onCancel={() => setReinstating(false)}
          onConfirm={async (reason) => {
            await api.post(`/admin/users/${id}/reinstate`, { reason });
            toast.toast('success', `${u.fullName} reinstated.`);
            setReinstating(false); user.reload();
          }}
        />
      )}
    </div>
  );
}

/**
 * Moving an operator to a different platform role.
 *
 * Only the roles the signer could grant are offered, and the API applies the
 * same rule. The role is replaced rather than added, and their sessions end so
 * the change is felt at once rather than when a token expires.
 */
function ChangeRoleDialog({ userId, fullName, current, currentLabels, onClose, onDone }: {
  userId: string; fullName: string; current: string[]; currentLabels: string[];
  onClose: () => void; onDone: (roleName: string) => void;
}) {
  const roles = useQuery<{ items: Array<{ key: string; name: string; description: string | null; permissionCount: number }> }>(
    () => api.get('/admin/platform-users/roles'), [],
  );
  const [roleKey, setRoleKey] = useState(current[0] ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const items = roles.data?.items ?? [];
  const chosen = items.find((r) => r.key === roleKey);
  const unchanged = current.length === 1 && current[0] === roleKey;

  async function save() {
    setBusy(true); setError(null);
    try {
      await api.put(`/admin/platform-users/${userId}/role`, { roleKey });
      onDone(chosen?.name ?? roleKey);
    } catch (e) { setError(errorMessage(e)); setBusy(false); }
  }

  const currentNames = currentLabels.join(', ') || 'no role';

  return (
    <Dialog
      title={`Change ${fullName}’s role`}
      onClose={busy ? () => undefined : onClose}
      footer={(
        <>
          <Button variant="secondary" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} loading={busy} disabled={!roleKey || unchanged}>Change role and sign them out</Button>
        </>
      )}
    >
      <div className="mt-3 space-y-4">
        <p className="text-small text-neutral-600">
          A platform operator holds one role. Choosing another <strong>replaces</strong> it —
          what they can do changes to exactly what the new role allows — and ends their open
          sessions so the change applies at their next sign-in.
        </p>
        <SearchSelect
          label="New role"
          required
          value={roleKey}
          onChange={setRoleKey}
          disabled={roles.loading}
          hint="Only roles you could grant yourself are listed."
          placeholder="Choose a role…"
          searchPlaceholder="Search roles…"
          options={items.map((r) => ({ value: r.key, label: r.name }))}
        />
        {!unchanged && chosen && (
          <div className="rounded-input border border-neutral-200 bg-neutral-50 px-3 py-2 text-small text-neutral-700">
            <span className="text-neutral-500">From</span> {currentNames} <span className="text-neutral-500">to</span>{' '}
            <span className="font-medium text-neutral-900">{chosen.name}</span>
            <span className="text-neutral-500"> ({chosen.permissionCount} permissions)</span>. Recorded in the audit trail.
            {roleHint(chosen) && <p className="mt-1">{roleHint(chosen)}</p>}
          </div>
        )}
        {roles.error && <Alert tone="danger">{roles.error}</Alert>}
        {error && <Alert tone="danger">{error}</Alert>}
      </div>
    </Dialog>
  );
}
